import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode; initialTheme?: Theme }> = ({ 
  children, 
  initialTheme = 'light'
}) => {
  const [theme, setThemeState] = useState<Theme>(initialTheme || 'light');

  // Apply theme when initialTheme changes (from database)
  useEffect(() => {
    if (initialTheme && initialTheme !== theme) {
      setThemeState(initialTheme);
    }
  }, [initialTheme]);

  // Apply theme to document when theme changes
  useEffect(() => {
    const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
    if (isDashboardRoute) {
      // Apply theme synchronously to prevent flash
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);

  // Simple setTheme - just updates UI, doesn't save to database
  const setTheme = (newTheme: Theme): void => {
    setThemeState(newTheme);
    const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
    if (isDashboardRoute) {
      document.documentElement.setAttribute('data-theme', newTheme);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

